import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, Button, Card, ErrorNotice, Icon, SimulatedTag, StatusPill, type Status } from '@hive/ui';
import { api, errorMessage, keeperUrl, type EntitlementDetails, type ScreenSetup, type VenueSetup } from '../api/client';
import { useLoad, useNow } from '../api/hooks';
import { Shell } from '../components/Shell';
import { LicenceDialog, RightsDialog, ScreenDialog } from '../components/SetupDialogs';
import { SyncCard } from '../components/SyncCard';
import { VenueFieldsForm, venueNumbers, type VenueForm } from '../components/VenueFields';
import { dateShort } from '../format';
import { AuditFeed } from './UsersPage';

const RIGHTS_STATUS: Record<EntitlementDetails['status'], { label: string; status: Status }> = {
  scheduled: { label: 'Starts later', status: 'idle' },
  active: { label: 'In force', status: 'healthy' },
  expiring: { label: 'Expiring', status: 'expiring' },
  expired: { label: 'Ended', status: 'offline' },
};
const LEVEL: Record<EntitlementDetails['level'], string> = { competition: 'Competition', fixture: 'Fixture', channel: 'Channel' };
const SCREEN_STATUS: Record<string, Status> = { online: 'healthy', offline: 'offline', unpaired: 'unpaired' };

function formOf(s: VenueSetup): VenueForm {
  const v = s.venue;
  const str = (x: unknown) => (x == null ? '' : String(x));
  return {
    name: v.name,
    code: str(v.code),
    externalRef: str(v.externalRef),
    slug: str(v.slug),
    area: str(v.area),
    city: str(v.city),
    country: str(v.country),
    address: str(v.address),
    timezone: v.timezone,
    lat: str(v.lat),
    lng: str(v.lng),
    capacity: str(v.capacity),
  };
}

type Open =
  | { kind: 'screen'; screen?: ScreenSetup }
  | { kind: 'licence' }
  | { kind: 'rights'; right?: EntitlementDetails }
  | { kind: 'remove-screen'; screen: ScreenSetup }
  | null;

/** One venue's setup: details, screens, licence, rights and change history. */
export function VenueSetupPage() {
  const { venueId = '' } = useParams();
  const now = useNow(30_000);
  const setup = useLoad(async () => (await api.GET('/v1/setup/venues/{venueId}', { params: { path: { venueId } } })).data, [venueId]);
  const catalogue = useLoad(async () => (await api.GET('/v1/setup/rights-catalogue')).data);
  const history = useLoad(
    async () => (await api.GET('/v1/audit', { params: { query: { targetType: 'venue', targetId: venueId, limit: 40 } } })).data,
    [venueId],
  );
  const [data, setData] = useState<VenueSetup | null>(null);
  const [form, setForm] = useState<VenueForm | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (setup.data) {
      setData(setup.data);
      setForm(formOf(setup.data));
    }
  }, [setup.data]);

  const apply = (next: VenueSetup) => {
    setData(next);
    setForm(formOf(next));
    void history.reload();
  };

  async function saveDetails(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    const nums = venueNumbers(form);
    if (nums.error) return setError(nums.error);
    setBusy(true);
    setError(null);
    setSaved(false);
    const { data: next, error: err } = await api.PATCH('/v1/setup/venues/{venueId}', {
      params: { path: { venueId } },
      body: {
        name: form.name.trim(),
        timezone: form.timezone.trim(),
        code: form.code,
        externalRef: form.externalRef,
        slug: form.slug.trim(),
        area: form.area,
        city: form.city,
        country: form.country,
        address: form.address,
        ...(nums.lat !== undefined ? { lat: nums.lat, lng: nums.lng } : {}),
        ...(nums.capacity !== undefined ? { capacity: nums.capacity } : {}),
      },
    });
    setBusy(false);
    if (!next) return setError(errorMessage(err));
    apply(next);
    setSaved(true);
  }

  async function setActive(active: boolean) {
    setBusy(true);
    const { data: next, error: err } = await api.PATCH('/v1/setup/venues/{venueId}', { params: { path: { venueId } }, body: { active } });
    setBusy(false);
    if (!next) return setError(errorMessage(err));
    apply(next);
  }

  async function removeScreen(screen: ScreenSetup) {
    setBusy(true);
    const { data: next, error: err } = await api.DELETE('/v1/setup/venues/{venueId}/screens/{screenId}', { params: { path: { venueId, screenId: screen.id } } });
    setBusy(false);
    setOpen(null);
    if (!next) return setError(errorMessage(err));
    apply(next);
  }

  async function endRight(right: EntitlementDetails) {
    setBusy(true);
    const { data: next, error: err } = await api.DELETE('/v1/setup/venues/{venueId}/entitlements/{entitlementId}', {
      params: { path: { venueId, entitlementId: right.id } },
    });
    setBusy(false);
    if (!next) return setError(errorMessage(err));
    apply(next);
  }

  const v = data?.venue;
  const sim = v?.source === 'simulated';
  const rights = data?.entitlements ?? [];
  const current = rights.filter((r) => r.status !== 'expired');
  const ended = rights.filter((r) => r.status === 'expired');

  return (
    <Shell network={null} live>
      <main className="mc-page">
        <Breadcrumbs
          items={[{ label: 'Venues', href: '/venues' }, { label: v?.name ?? '…' }, { label: 'Setup' }]}
          renderLink={(href, label) => <Link to={href}>{label}</Link>}
        />
        {setup.error && <p className="mc-note mc-note--bad">Could not load this venue. It may not exist, or may belong to another tenant.</p>}
        {v && form && (
          <>
            <div className="mc-pagehead">
              <div className="mc-pagehead__left">
                <h1 className="mc-h1">{v.name}</h1>
                {sim && <SimulatedTag title="Simulated venue" />}
                {v.code && <span className="mc-pill-soft">{v.code}</span>}
                {v.active ? <StatusPill status="healthy">Active</StatusPill> : <StatusPill status="idle">Inactive</StatusPill>}
              </div>
              <div className="mc-pagehead__actions">
                {v.active && (
                  <Link to={`/venue/${v.id}`} className="hv-btn hv-btn--secondary">
                    Open live view
                  </Link>
                )}
                <Button onClick={() => setActive(!v.active)} disabled={busy}>
                  {v.active ? 'Deactivate' : 'Reactivate'}
                </Button>
              </div>
            </div>
            {!v.active && <p className="mc-note mc-note--info">Inactive: hidden from Keeper and the network map. Its players keep their last configuration.</p>}
            {error && <ErrorNotice title={error} />}

            <div className="mc-setup-grid">
              <div className="mc-col">
                <Card padded className="mc-side-card">
                  <div className="mc-row-between">
                    <span className="mc-section-title">Details</span>
                    {v.slug && (
                      <a className="mc-small" href={`${keeperUrl}/p/${v.slug}`} target="_blank" rel="noreferrer">
                        Patron page: /p/{v.slug}
                      </a>
                    )}
                  </div>
                  <form onSubmit={saveDetails} className="mc-form">
                    <VenueFieldsForm form={form} onChange={(f) => (setForm(f), setSaved(false))} showSlug />
                    <div className="mc-row-between">
                      <span className="mc-small hv-muted">{saved ? 'Saved. Players pick up the change straight away.' : ''}</span>
                      <Button variant="primary" type="submit" disabled={busy}>
                        Save details
                      </Button>
                    </div>
                  </form>
                </Card>

                <Card padded className="mc-side-card">
                  <div className="mc-row-between">
                    <span className="mc-section-title">Screens</span>
                    <Button size="sm" onClick={() => setOpen({ kind: 'screen' })}>
                      <Icon name="plus" size={16} />
                      Add screen
                    </Button>
                  </div>
                  {data.screens.length === 0 && <p className="mc-small hv-muted">No screens yet. Add one, then pair a player to it from Keeper.</p>}
                  <ul className="mc-rows">
                    {data.screens.map((s) => (
                      <li key={s.id} className="mc-rows__row">
                        <Icon name="screen" size={18} />
                        <span className="mc-grow">
                          <span className="mc-strong">{s.name}</span>
                          <span className="mc-small hv-muted mc-line">
                            {[s.area?.name, s.code, s.sizeInches && `${s.sizeInches}"`, s.connection].filter(Boolean).join(' · ') || 'No details'}
                          </span>
                        </span>
                        <StatusPill status={SCREEN_STATUS[s.status ?? 'unpaired'] ?? 'unpaired'}>
                          {s.status === 'online' ? 'Online' : s.status === 'offline' ? 'Offline' : 'Not paired'}
                        </StatusPill>
                        <Button variant="link" onClick={() => setOpen({ kind: 'screen', screen: s })}>
                          Edit
                        </Button>
                        <Button variant="link" className="mc-link-danger" onClick={() => setOpen({ kind: 'remove-screen', screen: s })}>
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                </Card>

                {!sim && <SyncCard setup={data} onSaved={apply} />}
              </div>

              <div className="mc-col">
                <Card padded className="mc-side-card">
                  <div className="mc-row-between">
                    <span className="mc-section-title">
                      <Icon name="licence" size={16} />
                      &nbsp;Exhibition licence
                    </span>
                    <Button size="sm" onClick={() => setOpen({ kind: 'licence' })}>
                      {data.licence ? 'Update' : 'Record licence'}
                    </Button>
                  </div>
                  {data.licence ? (
                    <div className="mc-kv">
                      <span className="hv-muted">Number</span>
                      <span>
                        {data.licence.licenceNo} {data.licence.source === 'simulated' && <SimulatedTag />}
                      </span>
                      <span className="hv-muted">Tier</span>
                      <span>{data.licence.tier}</span>
                      <span className="hv-muted">Valid</span>
                      <span>
                        {dateShort(data.licence.validFrom)} to {dateShort(data.licence.validTo)}{' '}
                        <StatusPill status={RIGHTS_STATUS[data.licence.status].status}>{RIGHTS_STATUS[data.licence.status].label}</StatusPill>
                      </span>
                      {data.licence.billing && (
                        <>
                          <span className="hv-muted">Billing</span>
                          <span>{data.licence.billing}</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="mc-note mc-note--bad">No licence recorded. Venue health counts this as a problem.</p>
                  )}
                </Card>

                <Card padded className="mc-side-card">
                  <div className="mc-row-between">
                    <span className="mc-section-title">Rights</span>
                    <Button size="sm" onClick={() => setOpen({ kind: 'rights' })} disabled={!catalogue.data}>
                      <Icon name="plus" size={16} />
                      Grant rights
                    </Button>
                  </div>
                  {current.length === 0 && <p className="mc-note mc-note--bad">No rights in force: the picker and the TV home will offer only sponsor loops.</p>}
                  <ul className="mc-rows">
                    {current.map((r) => (
                      <RightRow key={r.id} right={r} onEdit={() => setOpen({ kind: 'rights', right: r })} onEnd={() => endRight(r)} busy={busy} />
                    ))}
                  </ul>
                  {ended.length > 0 && (
                    <details className="mc-details">
                      <summary className="mc-small">Ended ({ended.length})</summary>
                      <ul className="mc-rows">
                        {ended.map((r) => (
                          <RightRow key={r.id} right={r} busy={busy} />
                        ))}
                      </ul>
                    </details>
                  )}
                </Card>

                <Card padded className="mc-side-card">
                  <span className="mc-section-title">
                    <Icon name="history" size={16} />
                    &nbsp;History
                  </span>
                  <AuditFeed events={history.data ?? []} now={now} />
                </Card>
              </div>
            </div>
          </>
        )}
      </main>

      {open?.kind === 'screen' && data && <ScreenDialog venueId={venueId} screen={open.screen} onClose={() => setOpen(null)} onSaved={apply} />}
      {open?.kind === 'licence' && data && <LicenceDialog venueId={venueId} licence={data.licence} onClose={() => setOpen(null)} onSaved={apply} />}
      {open?.kind === 'rights' && data && catalogue.data && (
        <RightsDialog venueId={venueId} catalogue={catalogue.data} right={open.right} onClose={() => setOpen(null)} onSaved={apply} />
      )}
      {open?.kind === 'remove-screen' && (
        <div className="mc-scrim" onClick={() => setOpen(null)}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="remove-title" className="mc-dialog" style={{ width: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="mc-dialog__head">
              <h1 id="remove-title" className="mc-dialog__title">
                Remove {open.screen.name}?
              </h1>
            </div>
            <div className="mc-dialog__body">
              <p className="mc-small">
                {open.screen.paired
                  ? 'Its player is unpaired and goes back to showing a pairing code. Its history stays in the audit log.'
                  : 'Its history stays in the audit log.'}
              </p>
            </div>
            <div className="mc-dialog__foot">
              <Button onClick={() => setOpen(null)}>Keep</Button>
              <Button variant="primary" className="mc-btn-danger" onClick={() => removeScreen(open.screen)} disabled={busy}>
                Remove screen
              </Button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function RightRow({ right: r, onEdit, onEnd, busy }: { right: EntitlementDetails; onEdit?(): void; onEnd?(): void; busy: boolean }) {
  const s = RIGHTS_STATUS[r.status];
  return (
    <li className="mc-rows__row">
      <span className="mc-grow">
        <span className="mc-strong">
          {r.label} {r.source === 'simulated' && <SimulatedTag />}
        </span>
        <span className="mc-small hv-muted mc-line">
          {[LEVEL[r.level], r.territory, r.scope, `${dateShort(r.validFrom)} to ${dateShort(r.validTo)}`].filter(Boolean).join(' · ')}
        </span>
      </span>
      <StatusPill status={s.status}>{s.label}</StatusPill>
      {onEdit && (
        <Button variant="link" onClick={onEdit}>
          Edit
        </Button>
      )}
      {onEnd && (
        <Button variant="link" className="mc-link-danger" onClick={onEnd} disabled={busy}>
          {r.status === 'scheduled' ? 'Cancel' : 'End now'}
        </Button>
      )}
    </li>
  );
}
